import React from 'react';
import { useIntl } from 'react-intl';
import {
    Popover,
    Box,
    Typography,
    Card,
    Button,
    IconButton,
    Tooltip,
    Chip,
    CircularProgress,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const ERROR_MESSAGE_ID_BY_TYPE = {
    limit: 'ai.suggestion.limit.reached',
    not_enabled: 'ai.suggestion.not.enabled',
    unavailable: 'ai.suggestion.unavailable',
};

const resolveErrorMessageId = (errorType) => ERROR_MESSAGE_ID_BY_TYPE[errorType] || 'ai.suggestion.error';

const SuggestionCard = ({ suggestion, onUse, onCopy, onDiscard, intl }) => {
    const sources = suggestion.sources || [];

    const handleCopy = () => {
        if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(suggestion.content || '');
        }
        onCopy?.(suggestion);
    };

    return (
        <Card
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 2,
                mb: 2,
                '&:last-child': { mb: 0 },
            }}
        >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {suggestion.content}
            </Typography>

            {sources.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.formatMessage({ id: 'ai.suggestion.sources' })}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {sources.map((source, index) => (
                            <Chip key={index} label={source} size="small" variant="outlined" />
                        ))}
                    </Box>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
                <Tooltip title={intl.formatMessage({ id: 'ai.suggestion.discard' })}>
                    <IconButton size="small" onClick={() => onDiscard?.(suggestion)}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={intl.formatMessage({ id: 'ai.suggestion.copy' })}>
                    <IconButton size="small" onClick={handleCopy}>
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => onUse?.(suggestion)}
                    sx={{
                        textTransform: 'none',
                        '&&&': { color: '#fff !important' },
                        '&&&:hover': { color: '#fff !important', backgroundColor: 'primary.main !important' }
                    }}
                >
                    {intl.formatMessage({ id: 'ai.suggestion.use' })}
                </Button>
            </Box>
        </Card>
    );
};

const AiSuggestionPanel = ({
    open,
    anchorEl,
    onClose,
    loading,
    error,
    errorType,
    suggestions,
    outdated,
    onUse,
    onCopy,
    onDiscard,
    onRegenerate,
    onGenerate,
}) => {
    const intl = useIntl();
    const suggestionList = suggestions || [];

    return (
        <Popover
            open={Boolean(open)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
            <Box sx={{ width: 400, maxWidth: '90vw', maxHeight: '70vh', overflowY: 'auto', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <SmartToyOutlinedIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                        {intl.formatMessage({ id: 'ai.suggestion.title' })}
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 3 }}>
                        <CircularProgress size={28} color="primary" />
                        <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({ id: 'ai.suggestion.loading' })}
                        </Typography>
                    </Box>
                )}

                {!loading && error && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 3, textAlign: 'center' }}>
                        <ErrorOutlineIcon color="error" />
                        <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({ id: resolveErrorMessageId(errorType) })}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<AutorenewIcon />}
                            onClick={onRegenerate}
                            sx={{ textTransform: 'none' }}
                        >
                            {intl.formatMessage({ id: 'ai.suggestion.regenerate' })}
                        </Button>
                    </Box>
                )}

                {!loading && !error && outdated && suggestionList.length > 0 && (
                    <Alert
                        severity="warning"
                        action={
                            <Button
                                size="small"
                                color="inherit"
                                startIcon={<AutorenewIcon />}
                                onClick={onRegenerate}
                                sx={{ textTransform: 'none' }}
                            >
                                {intl.formatMessage({ id: 'ai.suggestion.regenerate' })}
                            </Button>
                        }
                        sx={{ mb: 1.5 }}
                    >
                        {intl.formatMessage({ id: 'ai.suggestion.outdated' })}
                    </Alert>
                )}

                {!loading && !error && suggestionList.length === 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 3, textAlign: 'center' }}>
                        <SmartToyOutlinedIcon color="primary" sx={{ fontSize: '2.5rem' }} />
                        <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({ id: 'ai.suggestion.intro.description' })}
                        </Typography>
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={onGenerate}
                            sx={{
                        textTransform: 'none',
                        '&&&': { color: '#fff !important' },
                        '&&&:hover': { color: '#fff !important', backgroundColor: 'primary.main !important' }
                    }}
                        >
                            {intl.formatMessage({ id: 'ai.suggestion.generate' })}
                        </Button>
                    </Box>
                )}

                {!loading && !error && suggestionList.length > 0 && (
                    <>
                        <Box>
                            {suggestionList.map((suggestion) => (
                                <SuggestionCard
                                    key={suggestion.id || suggestion.suggestionindex}
                                    suggestion={suggestion}
                                    onUse={onUse}
                                    onCopy={onCopy}
                                    onDiscard={onDiscard}
                                    intl={intl}
                                />
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                            <Button
                                size="small"
                                color="primary"
                                startIcon={<AutorenewIcon />}
                                onClick={onRegenerate}
                                sx={{ textTransform: 'none' }}
                            >
                                {intl.formatMessage({ id: 'ai.suggestion.regenerate' })}
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Popover>
    );
};

export default AiSuggestionPanel;
